import React from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Button } from './ui/button';
import { Globe, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';

export function LanguageSwitcher({ variant = 'default', compact = false }) {
    const { i18n } = useTranslation();
    const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language?.split('-')[0]) || SUPPORTED_LANGUAGES[0];

    const change = (code) => {
        i18n.changeLanguage(code);
        // If user is logged in, sync language preference to backend (non-blocking)
        try {
            if (typeof window !== 'undefined' && localStorage.getItem('cantiere_token')) {
                import('../lib/api').then(({ api }) => {
                    api.put('/auth/me/language', { language: code }).catch(() => {});
                });
            }
        } catch (_) { /* non-blocking */ }
    };

    // Compact = inline-flex (used in top navbars). Default = w-full justify-start (sidebar use-case).
    const triggerLayout = compact
        ? 'inline-flex w-auto justify-center px-2.5'
        : 'w-full justify-start';
    const triggerHeight = compact ? 'h-9 min-h-[36px]' : 'h-11 min-h-[44px]';

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className={`gap-1.5 ${triggerHeight} ${triggerLayout} ${variant === 'dark' ? 'text-white hover:bg-white/10 bg-white/5 border border-white/10' : ''}`}
                    data-testid="language-switcher"
                    aria-label="Change language"
                >
                    <Globe className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-medium">{current.flag} {current.code.toUpperCase()}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" side="top" className="w-44 p-1 z-[60]">
                {SUPPORTED_LANGUAGES.map((lang) => {
                    const isActive = lang.code === current.code;
                    return (
                        <button
                            key={lang.code}
                            onClick={() => change(lang.code)}
                            className={`w-full flex items-center gap-3 px-2.5 py-2 rounded text-sm hover:bg-[hsl(var(--muted))] transition-colors text-left min-h-[44px] ${isActive ? 'bg-[hsl(var(--muted))]' : ''}`}
                            data-testid={`lang-option-${lang.code}`}
                        >
                            <span className="text-lg">{lang.flag}</span>
                            <span className="flex-1 font-medium">{lang.native}</span>
                            {isActive && <Check className="w-4 h-4 text-[hsl(var(--accent))]" />}
                        </button>
                    );
                })}
            </PopoverContent>
        </Popover>
    );
}
